import { apiClient } from '@app/api';
import { ContentCluster } from '@app/components/base/content-structure/ContentCluster';
import { Page } from '@app/components/page/Page';
import { Client } from '@app/core/types/client';
import { Paginated, User } from '@mi-front/core';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { AutocompleteChangeReason } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { orderBy } from 'lodash';
import { SyntheticEvent } from 'react';
import { AutocompleteElement, FormContainer } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { CurrentImpersonatedUserCard } from '../components/CurrentImpersonatedUserCard';
import { ImpersonationAutocompleteOption } from '../components/ImpersonationAutocompleteOption';
import { RecentUsersQuickLogin } from '../components/RecentUsersQuickLogin';
import { useImpersonation } from '../hooks/useImpersonationStore';
import { impersonationGroupMessages, impersonationMessages } from '../impersonation-messages';
import { TabDeepLinkUserCluster, useTabAuthAsUser } from '../tab-auth-as';
import {
  getImpersonationGroupOrder,
  getImpersonationUserGroupKey,
} from '../impersonation-user-groups';

type FormData = {
  user: (User & { client: Client }) | null;
};

/**
 * Admin screen to search for a user and start impersonation (or re-open a recent one).
 */
export function ImpersonationPage() {
  const { setImpersonatedUser } = useImpersonation();
  const { $t } = useIntl();
  const title = $t(impersonationMessages.pageTitle);

  const usersQuery = useQuery<Paginated<User & { client: Client }>>({
    queryKey: ['admin-all-users'],
    queryFn: () =>
      apiClient.get('/admin/users', {
        params: {
          contain: 'client',
          pageSize: 2000,
        },
      }),
  });

  const usersWithClient = usersQuery.data?.results.filter((u) => u.client) ?? [];
  const tabDeepLinkUser = useTabAuthAsUser(usersWithClient);
  const options = orderBy(
    usersWithClient,
    [(u) => getImpersonationGroupOrder(getImpersonationUserGroupKey(u)), 'client.name', 'email'],
    ['asc', 'asc', 'asc'],
  );

  /** Starts impersonation and sends the user to the app root (same as recent-user quick login). */
  function startImpersonation(user: User & { client: Client }) {
    setImpersonatedUser(user);
    setTimeout(() => {
      window.location.href = '/';
    }, 0);
  }

  function handleAutocompleteUserChange(
    _event: SyntheticEvent,
    value: FormData['user'],
    reason: AutocompleteChangeReason,
  ) {
    if (reason !== 'selectOption' || !value) {
      return;
    }
    startImpersonation(value);
  }

  return (
    <Page htmlTitle={title}>
      <Page.Header icon={<AdminPanelSettingsIcon />} title={title} />
      <Page.Content maxWidth="md">
        <ContentCluster message={impersonationMessages.searchLabel} divider={false}>
          <FormContainer
            FormProps={{
              onSubmit: (event) => event.preventDefault(),
            }}
            defaultValues={{
              user: null,
            }}
          >
            <AutocompleteElement
              name="user"
              options={options}
              loading={usersQuery.isLoading}
              textFieldProps={{
                margin: 'none',
                fullWidth: true,
                // Title is on ContentCluster; no TextField label so the outline stays continuous.
                placeholder: $t(impersonationMessages.searchLabel),
              }}
              autocompleteProps={{
                disabled: usersQuery.isLoading,
                // Highlight the first match so Enter selects it (same idea as station navigator).
                autoHighlight: true,
                openOnFocus: true,
                slotProps: {
                  listbox: {
                    sx: { maxHeight: 'calc(100vh - 300px)' },
                  },
                },
                groupBy: (option) =>
                  $t(impersonationGroupMessages[getImpersonationUserGroupKey(option)]),
                getOptionLabel: (option) =>
                  option ? `${option.client?.name} - ${option.email}` : '',
                isOptionEqualToValue: (option, value) => option?.id === value?.id,
                renderOption: (props, option, { inputValue, index }) => (
                  <ImpersonationAutocompleteOption
                    optionProps={props}
                    user={option}
                    showEnterHint={index === 0 && inputValue.trim() !== ''}
                  />
                ),
                filterOptions: (options, { inputValue }) => {
                  return options.filter((option) =>
                    `${option.client?.name || ''} ${option.email} ${option.name} ${option.surname} ${
                      option.role
                    }`
                      .trim()
                      .toLowerCase()
                      .includes(inputValue.trim().toLowerCase()),
                  );
                },
                onChange: handleAutocompleteUserChange,
              }}
            />
          </FormContainer>
        </ContentCluster>

        {tabDeepLinkUser ? <TabDeepLinkUserCluster user={tabDeepLinkUser} /> : null}
        <CurrentImpersonatedUserCard />
        <RecentUsersQuickLogin onUserSelect={startImpersonation} />
      </Page.Content>
    </Page>
  );
}
